import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { Brain, TrendingDown, AlertTriangle, CheckCircle, Lightbulb, RefreshCw } from 'lucide-react';

const AiInsights = () => {
  const { t, i18n } = useTranslation();
  const { currentBusiness } = useAuth();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInsights();
    const handleBusinessChange = () => fetchInsights();
    window.addEventListener('businessChanged', handleBusinessChange);
    return () => window.removeEventListener('businessChanged', handleBusinessChange);
  }, [currentBusiness]);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const res = await api.get('/ai/insights');
      setInsights(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <AlertTriangle size={20} className="text-red-400" />;
      case 'warning': return <TrendingDown size={20} className="text-yellow-400" />;
      case 'positive': return <CheckCircle size={20} className="text-emerald-400" />;
      default: return <Lightbulb size={20} className="text-blue-400" />;
    }
  };

  const getSeverityBg = (severity) => {
    switch (severity) {
      case 'critical': return 'border-red-500/20 bg-red-500/5';
      case 'warning': return 'border-yellow-500/20 bg-yellow-500/5';
      case 'positive': return 'border-emerald-500/20 bg-emerald-500/5';
      default: return 'border-blue-500/20 bg-blue-500/5';
    }
  };

  const getSeverityLabel = (severity) => {
    const lang = i18n.language;
    switch (severity) {
      case 'critical': return lang === 'sw' ? 'Muhimu' : 'Critical';
      case 'warning': return lang === 'sw' ? 'Onyo' : 'Warning';
      case 'positive': return lang === 'sw' ? 'Nzuri' : 'Positive';
      default: return lang === 'sw' ? 'Ushauri' : 'Tip';
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-500 text-sm">
          {i18n.language === 'sw' ? 'Inachambua biashara yako...' : 'Analyzing your business...'}
        </p>
      </div>
    </div>
  );

  const title = i18n.language === 'sw' ? 'Uchambuzi wa AI' : 'AI Insights';
  const subtitle = i18n.language === 'sw' ? 'Uchambuzi mahiri wa biashara yako' : 'Smart analysis for your business';
  const refreshLabel = i18n.language === 'sw' ? 'Fanya Uchambuzi Upya' : 'Refresh Analysis';
  const basedOn = i18n.language === 'sw' ? 'Kulingana na data ya siku 30 zilizopita' : 'Based on your last 30 days of data';
  const foundLabel = i18n.language === 'sw' ? 'taarifa zimepatikana' : 'insights found';
  const noInsightsTitle = i18n.language === 'sw' ? 'Hakuna taarifa bado' : 'No insights yet';
  const noInsightsDesc = i18n.language === 'sw' ? 'Ongeza mauzo, gharama na stock ili kupata uchambuzi.' : 'Add more sales, expenses, and stock data to get AI-powered insights.';
  const viewAllLabel = i18n.language === 'sw' ? 'Ona Zote' : 'View All';

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white">{title}</h2>
          <p className="text-gray-400 text-sm mt-1">{subtitle}</p>
        </div>
        <button onClick={fetchInsights} className="flex items-center gap-2 bg-purple-500/20 text-purple-400 px-4 py-2.5 rounded-xl font-medium hover:bg-purple-500/30 transition text-sm border border-purple-500/20">
          <RefreshCw size={16} /> {refreshLabel}
        </button>
      </div>

      {insights && (
        <div className="bg-gradient-to-r from-purple-500/5 to-blue-500/5 border border-purple-500/20 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 bg-purple-500/20 rounded-xl"><Brain size={24} className="text-purple-400" /></div>
            <div>
              <p className="text-white font-bold">{insights.totalInsights} {foundLabel}</p>
              <p className="text-gray-400 text-xs">{basedOn}</p>
            </div>
          </div>
        </div>
      )}

      {insights?.insights?.length > 0 ? (
        <div className="space-y-3">
          {insights.insights.map((insight, i) => (
            <div key={i} className={`border rounded-xl p-4 flex items-start gap-3 ${getSeverityBg(insight.severity)}`}>
              <div className="mt-0.5">{getSeverityIcon(insight.severity)}</div>
              <div className="flex-1">
                <p className="text-white text-sm">
                  {i18n.language === 'sw' ? insight.text_sw : insight.text_en}
                </p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${
                insight.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                insight.severity === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-emerald-500/20 text-emerald-400'
              }`}>{getSeverityLabel(insight.severity)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-[#0d1410] border border-white/5 rounded-2xl p-12 text-center">
          <Brain size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 font-medium">{noInsightsTitle}</p>
          <p className="text-gray-600 text-sm mt-1">{noInsightsDesc}</p>
        </div>
      )}
    </div>
  );
};

export default AiInsights;
